import { CalendarDays } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useDayEvents } from '../DayEventsModal';
import { useCalendar, useHasCalendarSource, useOutlookCalendars, type AgendaEvent } from '../../hooks/useWidgetsData';
import { useSharedSource } from '../../store/screen';
import { useSession } from '../../store/session';
import { useNow } from '../../hooks/useNow';
import { addDays, calendarRange, dateKey } from '../../lib/calendarRange';
import { useSettings } from '../../store/settings';

/** All-day events are dates, not instants: show them on their calendar day regardless of zone. */
function localStart(e: AgendaEvent): Date {
  if (!e.allDay) return new Date(e.start);
  const d = new Date(e.start);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function localEnd(e: AgendaEvent): Date {
  if (!e.allDay) return new Date(e.end);
  const d = new Date(e.end);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Does the event cover any part of local day `day` (00:00–24:00)? */
function onDay(e: AgendaEvent, day: Date): boolean {
  const s = localStart(e);
  const end = localEnd(e);
  const dayEnd = addDays(day, 1);
  return s < dayEnd && (end > day || (s >= day && end.getTime() === s.getTime()));
}

function dayLabel(d: Date, today: Date): string {
  if (dateKey(d) === dateKey(today)) return 'Today';
  if (dateKey(d) === dateKey(addDays(today, 1))) return 'Tomorrow';
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'short' }).format(d);
}

/** One-at-a-time: the calendar shown and tabs for every calendar switched on. */
interface Rotation {
  /** null = all calendars together. */
  activeId: string | null;
  tabs: ReactNode;
}

/**
 * Calendar → One at a time: every calendar that's switched on (Outlook calendars not hidden,
 * then enabled iCal feeds, then any others seen in the events, e.g. a shared canvas's
 * Outlook), shown in turn with tabs, like the Planner widget. Events are already loaded for
 * all of them, so switching costs nothing.
 */
function useCalendarRotation(events: AgendaEvent[]): Rotation {
  const { display, rotateSec, sources, outlook, outlookHidden } = useSettings((s) => s.calendar);
  const rotate = display === 'rotate';
  const microsoft = useSession((s) => s.microsoft?.connected ?? false);
  const sharedOutlook = useSharedSource('outlook');
  const outlookCalendars = useOutlookCalendars(rotate && outlook && !sharedOutlook);
  const list: Array<{ id: string; name: string; color: string }> = [];
  if (rotate) {
    if (outlook && microsoft && !sharedOutlook)
      for (const c of outlookCalendars.data ?? []) if (!outlookHidden.includes(c.id)) list.push({ id: `outlook:${c.id}`, name: c.name, color: c.color });
    for (const s of sources) if (s.enabled) list.push({ id: `ics:${s.id}`, name: s.name, color: s.color });
    for (const e of events) if (!list.some((c) => c.id === e.calendar.id)) list.push(e.calendar);
  }
  const [index, setIndex] = useState(0);
  useEffect(() => {
    if (list.length < 2 || rotateSec <= 0) return;
    const t = window.setInterval(() => setIndex((i) => (i + 1) % list.length), rotateSec * 1000);
    return () => window.clearInterval(t);
  }, [list.length, rotateSec, index]);
  if (!rotate || list.length < 2) return { activeId: null, tabs: null };
  const active = list[index % list.length]!;
  return {
    activeId: active.id,
    tabs: (
      <div className="flex flex-wrap gap-1" role="tablist" aria-label="Calendars">
        {list.map((c, i) => (
          <button
            key={c.id}
            role="tab"
            aria-selected={c.id === active.id}
            onClick={() => setIndex(i)}
            className={`flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs normal-case tracking-normal transition ${
              c.id === active.id ? 'bg-[var(--accent)] text-black' : 'opacity-70 hover:opacity-100'
            }`}
          >
            <span className="h-2 w-2 rounded-full" style={{ background: c.color }} aria-hidden />
            {c.name}
          </button>
        ))}
      </div>
    ),
  };
}

function Shell({ title, children, fill = false, tabs }: { title: string; children: ReactNode; fill?: boolean; tabs?: ReactNode }) {
  const { showHeader, textSize, todayColor } = useSettings((s) => s.calendar);
  return (
    <div className={`glass p-4 ${fill ? 'flex flex-col' : ''}`} style={{ '--cal-today': todayColor || 'var(--accent)' } as CSSProperties}>
      {/* zoom scales the whole calendar, including its rem-sized text, by the chosen text size. */}
      <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''} style={{ zoom: textSize }}>
        {(showHeader || tabs) && (
          <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            {showHeader && (
              <div className="font-heading flex items-center gap-2 text-sm font-medium uppercase tracking-wider opacity-80">
                <CalendarDays className="h-4 w-4" aria-hidden /> {title}
              </div>
            )}
            {tabs && <div className="ml-auto">{tabs}</div>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

type EventStyle = 'dot' | 'bar' | 'block';

/** Marks an event in its calendar's colour: a tinted block, a bar down the left edge, or nothing (a dot is drawn separately). */
function eventLook(style: EventStyle, color: string): { className: string; style: CSSProperties } {
  if (style === 'block') return { className: 'rounded-md px-2 py-1', style: { background: `${color}33`, borderLeft: `3px solid ${color}` } };
  if (style === 'bar') return { className: 'border-l-4 pl-2', style: { borderColor: color } };
  return { className: '', style: {} };
}

/**
 * `fill`: the calendar has a fixed-size box (Custom layout). Week and month grids then
 * stretch to the full height, and each day shows as many events as fit.
 */
export function Calendar({ maxEvents = 8, fill = false }: { maxEvents?: number; fill?: boolean }) {
  const hasSource = useHasCalendarSource();
  const { view, days, weekStartsOn, eventStyle, showLocation, showEndTime } = useSettings((s) => s.calendar);
  const hour12 = useSettings((s) => s.clock.hour12);
  const now = useNow('minute');
  const range = calendarRange(view, days, weekStartsOn, now);
  const { data, error, loading } = useCalendar({ start: dateKey(range.first), days: range.days });
  const rotation = useCalendarRotation(data ?? []);

  if (!hasSource) {
    return (
      <div className="glass muted p-4 text-sm">
        Connect Outlook or add a calendar address (iCloud, Google or Outlook .ics link) in Settings → Widgets → Calendar.
      </div>
    );
  }

  const fmtTime = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12 });
  const status = !data && <div className="muted text-sm">{loading ? 'Loading events…' : error}</div>;
  // One at a time: just the calendar on screen.
  const events = (data ?? []).filter((e) => !rotation.activeId || e.calendar.id === rotation.activeId);

  if (view === 'day') {
    return (
      <Shell title={new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'long' }).format(now)} tabs={rotation.tabs}>
        {status}
        {data && <DayTimeline events={events.filter((e) => onDay(e, range.first))} day={range.first} now={now} fmtTime={fmtTime} />}
      </Shell>
    );
  }

  if (view === 'week' || view === 'month') {
    const title =
      view === 'week'
        ? `Week of ${new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' }).format(range.first)}`
        : new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(now);
    return (
      <Shell title={title} fill={fill} tabs={rotation.tabs}>
        {status}
        <Grid range={range} month={view === 'month' ? now.getMonth() : null} events={events} today={now} fmtTime={fmtTime} fill={fill} />
      </Shell>
    );
  }

  // list (agenda)
  const upcoming = events.filter((e) => new Date(e.end) > now).slice(0, maxEvents);
  const groups = new Map<string, { label: string; events: AgendaEvent[] }>();
  for (const e of upcoming) {
    const start = localStart(e);
    const shown = start < now ? now : start;
    const key = dateKey(shown);
    const g = groups.get(key) ?? { label: dayLabel(shown, now), events: [] };
    g.events.push(e);
    groups.set(key, g);
  }
  return (
    <Shell title="Up next" tabs={rotation.tabs}>
      {status}
      {data && upcoming.length === 0 && <div className="muted text-sm">Nothing scheduled. Enjoy the calm.</div>}
      <div className="space-y-3">
        {[...groups.values()].map((g) => (
          <div key={g.label}>
            <div
              className={`mb-1 text-xs font-semibold uppercase tracking-wider ${g.label === 'Today' ? '' : 'muted'}`}
              style={g.label === 'Today' ? { color: 'var(--cal-today)' } : undefined}
            >
              {g.label}
            </div>
            <ul className="space-y-1.5">
              {g.events.map((e) => {
                const look = eventLook(eventStyle, e.calendar.color);
                return (
                  <li key={`${e.uid}-${e.start}`} className={`flex items-start gap-3 ${look.className}`} style={look.style}>
                    {eventStyle === 'dot' && <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: e.calendar.color }} />}
                    <span className={`muted shrink-0 text-sm tabular-nums ${showEndTime ? 'w-24' : 'w-14'}`}>
                      {e.allDay
                        ? 'All day'
                        : `${fmtTime.format(new Date(e.start))}${showEndTime ? `–${fmtTime.format(new Date(e.end))}` : ''}`}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{e.title}</span>
                      {showLocation && e.location && <span className="muted block truncate text-xs">{e.location}</span>}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </Shell>
  );
}

/** Hour-by-hour timeline for one day, with all-day events above and a "now" line. */
function DayTimeline({
  events,
  day,
  now,
  fmtTime,
}: {
  events: AgendaEvent[];
  day: Date;
  now: Date;
  fmtTime: Intl.DateTimeFormat;
}) {
  const { showLocation, showEndTime } = useSettings((s) => s.calendar);
  const allDay = events.filter((e) => e.allDay);
  const timed = events.filter((e) => !e.allDay);
  const hourOf = (d: Date) => (d < day ? 0 : d >= addDays(day, 1) ? 24 : d.getHours() + d.getMinutes() / 60);
  const firstHour = Math.min(8, ...timed.map((e) => Math.floor(hourOf(new Date(e.start)))));
  const lastHour = Math.max(20, ...timed.map((e) => Math.ceil(hourOf(new Date(e.end)))));
  const span = lastHour - firstHour;
  const pct = (h: number) => `${((h - firstHour) / span) * 100}%`;
  const nowH = hourOf(now);

  return (
    <div className="space-y-2">
      {allDay.map((e) => (
        <div key={e.uid} className="truncate rounded-md px-2 py-1 text-sm text-black" style={{ background: e.calendar.color }}>
          {e.title}
        </div>
      ))}
      <div className="relative" style={{ height: `${span * 2.25}rem` }}>
        {Array.from({ length: span + 1 }, (_, i) => firstHour + i).map((h) => (
          <div key={h} className="absolute inset-x-0 flex items-start gap-2" style={{ top: pct(h) }}>
            <span className="muted -mt-2 w-10 shrink-0 text-right text-xs tabular-nums">
              {fmtTime.format(new Date(day.getFullYear(), day.getMonth(), day.getDate(), h % 24))}
            </span>
            <span className="mt-0 h-px flex-1 bg-[var(--border)]" />
          </div>
        ))}
        {timed.map((e) => {
          const s = hourOf(new Date(e.start));
          const end = Math.max(hourOf(new Date(e.end)), s + 0.5);
          return (
            <div
              key={`${e.uid}-${e.start}`}
              className="absolute left-12 right-0 overflow-hidden rounded-md border-l-4 bg-white/10 px-2 py-0.5 text-sm"
              style={{ top: pct(s), height: `calc(${pct(end)} - ${pct(s)})`, borderColor: e.calendar.color }}
            >
              <div className="truncate font-medium">{e.title}</div>
              <div className="muted truncate text-xs">
                {fmtTime.format(new Date(e.start))}
                {showEndTime ? `–${fmtTime.format(new Date(e.end))}` : ''}
                {showLocation && e.location ? ` · ${e.location}` : ''}
              </div>
            </div>
          );
        })}
        {nowH > firstHour && nowH < lastHour && (
          <div className="absolute left-11 right-0 h-0.5 bg-red-500" style={{ top: pct(nowH) }} aria-label="Now">
            <span className="absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full bg-red-500" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Week (one row) or month (whole weeks) grid. */
function Grid({
  range,
  month,
  events,
  today,
  fmtTime,
  fill,
}: {
  range: { first: Date; days: number };
  month: number | null;
  events: AgendaEvent[];
  today: Date;
  fmtTime: Intl.DateTimeFormat;
  fill: boolean;
}) {
  const days = Array.from({ length: range.days }, (_, i) => addDays(range.first, i));
  const fmtDow = new Intl.DateTimeFormat(undefined, { weekday: month === null ? 'short' : 'narrow' });
  return (
    <div className={fill ? 'flex min-h-0 flex-1 flex-col' : ''}>
      <div className="grid grid-cols-7 gap-1 text-center text-xs">
        {days.slice(0, 7).map((d) => (
          <div key={dateKey(d)} className="muted font-semibold uppercase">
            {fmtDow.format(d)}
          </div>
        ))}
      </div>
      <div className={`mt-1 grid grid-cols-7 gap-1 ${fill ? 'min-h-0 flex-1' : ''}`} style={fill ? { gridAutoRows: 'minmax(0, 1fr)' } : undefined}>
        {days.map((d) => (
          <DayCell
            key={dateKey(d)}
            day={d}
            events={events.filter((e) => onDay(e, d))}
            isToday={dateKey(d) === dateKey(today)}
            outside={month !== null && d.getMonth() !== month}
            week={month === null}
            fill={fill}
            fmtTime={fmtTime}
          />
        ))}
      </div>
    </div>
  );
}

const EVENT_ROW = 'truncate rounded px-1 text-[0.7rem] leading-tight';

/**
 * One day in the week/month grid. In a fixed-size box it shows as many events as fit in
 * its height (measured with two invisible sample rows, so text size and zoom are allowed
 * for); otherwise 6 (week) or 2 (month). Hidden events are a "+N more" button away.
 */
function DayCell({
  day,
  events,
  isToday,
  outside,
  week,
  fill,
  fmtTime,
}: {
  day: Date;
  events: AgendaEvent[];
  isToday: boolean;
  outside: boolean;
  week: boolean;
  fill: boolean;
  fmtTime: Intl.DateTimeFormat;
}) {
  const eventStyle = useSettings((s) => s.calendar.eventStyle);
  const openDay = useDayEvents((s) => s.open);
  const cap = week ? 6 : 2;
  const list = useRef<HTMLDivElement>(null);
  const probe = useRef<HTMLUListElement>(null);
  const [rows, setRows] = useState(cap);

  useLayoutEffect(() => {
    if (!fill) {
      setRows(cap);
      return;
    }
    const box = list.current;
    const sample = probe.current;
    if (!box || !sample) return;
    const measure = () => {
      const [a, b] = Array.from(sample.children).map((li) => li.getBoundingClientRect());
      if (!a || !b || !a.height) return;
      const step = b.top - a.top;
      const room = box.getBoundingClientRect().height;
      setRows(room < a.height ? 0 : Math.floor((room - a.height) / step) + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [fill, cap]);

  // Everything fits, or keep the last row for "+N more".
  const shown = events.length <= rows ? events.length : Math.max(0, rows - 1);
  const hidden = events.length - shown;

  return (
    <div
      className={`flex min-w-0 flex-col rounded-md p-1 ${fill ? 'min-h-0 overflow-hidden' : week ? 'min-h-[8rem]' : 'min-h-[4rem]'} ${
        isToday ? 'bg-[var(--cal-today)]/15 ring-1 ring-[var(--cal-today)]' : 'bg-white/5'
      } ${outside ? 'opacity-40' : ''}`}
    >
      <div className={`text-right text-xs tabular-nums ${isToday ? 'font-bold text-[var(--cal-today)]' : 'muted'}`}>{day.getDate()}</div>
      <div ref={list} className={`relative mt-0.5 ${fill ? 'min-h-0 flex-1 overflow-hidden' : ''}`}>
        {/* Two invisible sample rows: their spacing is how tall one event row is here. */}
        {fill && (
          <ul ref={probe} className="pointer-events-none invisible absolute inset-x-0 top-0 space-y-0.5" aria-hidden>
            <li className={EVENT_ROW}>Sample</li>
            <li className={EVENT_ROW}>Sample</li>
          </ul>
        )}
        <ul className="space-y-0.5">
        {events.slice(0, shown).map((e) => (
          <li
            key={`${e.uid}-${e.start}`}
            className={EVENT_ROW}
            style={
              eventStyle === 'block'
                ? { background: `${e.calendar.color}33`, borderLeft: `3px solid ${e.calendar.color}` }
                : eventStyle === 'bar'
                  ? { borderLeft: `3px solid ${e.calendar.color}` }
                  : undefined
            }
            title={e.title}
          >
            {eventStyle === 'dot' && <span className="mr-1 inline-block h-1.5 w-1.5 rounded-full align-middle" style={{ background: e.calendar.color }} />}
            {week && !e.allDay && <span className="opacity-70">{fmtTime.format(new Date(e.start))} </span>}
            {e.title}
          </li>
        ))}
        {hidden > 0 && (
          <li className={EVENT_ROW}>
            <button type="button" className="muted w-full text-left hover:text-[var(--fg)] hover:underline" onClick={() => openDay(day, events)}>
              +{hidden} more
            </button>
          </li>
        )}
        </ul>
      </div>
    </div>
  );
}
